import { faFilePdf } from '@fortawesome/pro-regular-svg-icons/faFilePdf';
import { faImageLandscape } from '@fortawesome/pro-regular-svg-icons/faImageLandscape';
import { faImages } from '@fortawesome/pro-regular-svg-icons/faImages';
import { faMapMarkerAlt } from '@fortawesome/pro-regular-svg-icons/faMapMarkerAlt';
import { faPictureInPicture } from '@fortawesome/pro-regular-svg-icons/faPictureInPicture';
import { faVideo } from '@fortawesome/pro-regular-svg-icons/faVideo';
import { faVideoPlus } from '@fortawesome/pro-regular-svg-icons/faVideoPlus';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import useSlideshowBar from '@propertypal/shared/src/hooks/useSlideshowBar';
import ShopTheLook from '@propertypal/shared/src/resources/icons/misc/shop-the-look.svg';
import SitemapIcon from '@propertypal/shared/src/resources/icons/property/sitemap-black.svg';
import { Property } from '@propertypal/shared/src/types/property';
import { useRouter } from 'next/router';
import React, { CSSProperties, FunctionComponent, useEffect } from 'react';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { Button, CloseButton, Container, Content, FadeOver, Pill, Rail } from './SlideshowBar.style';

interface Props {
  containerStyle?: CSSProperties;
  getReplaceUrl: (url: string) => string;
  property: Property;
  hasSitemap?: boolean;
  hasFeaturedBrochure?: boolean;
  hasSlideshow?: boolean;
  replace: boolean;
  onClose?: () => void;
  maxWidth?: number;
  idPrefix: string;
  location: string;
}

const getTabId = (path: string) => {
  const urlParts = path.split('?')[0].split('/');

  if (urlParts.length >= 4) {
    return `tab-${urlParts[3]}`;
  }

  return '';
};

const SlideshowBar: FunctionComponent<Props> = (props) => {
  const router = useRouter();
  const subPage = String(router.query.subPage);
  const urlParts = router.asPath.split('?')[0].split('/');
  const tabId = getTabId(router.asPath);

  const { propertyVideo, propertyTour, images, propertyFloorPlans, handleTourClick, tourPath, videoPath, trackLink } =
    useSlideshowBar({
      property: props.property,
      getReplaceUrl: props.getReplaceUrl,
      location: props.location,
    });

  useEffect(() => {
    if (tabId) {
      const el = document.getElementById(`${props.idPrefix}-${tabId}`);
      el?.scrollIntoView({ inline: 'center' });
    }
  }, []);

  return (
    <Container style={{ paddingRight: props.onClose ? 50 : 0, ...props.containerStyle }}>
      <FadeOver style={{ right: props.onClose ? 50 : 0 }} />

      {props.onClose && (
        <CloseButton type="button" onClick={props.onClose} data-testid="slideshow-close">
          <FontAwesomeIcon icon={faTimes} style={{ fontSize: 30 }} />
        </CloseButton>
      )}

      <Rail id={props.idPrefix} style={{ maxWidth: props.maxWidth, margin: props.maxWidth ? '0 auto' : undefined }}>
        <Content>
          {props.hasSlideshow && images.length > 0 && (
            <Button
              id={`${props.idPrefix}-tab-slideshow`}
              href={props.getReplaceUrl('/slideshow')}
              shallow
              replace={props.replace}
              $active={tabId === 'tab-slideshow'}
              onClick={trackLink('Slideshow')}
            >
              <FontAwesomeIcon icon={faImageLandscape} size={18} />
              <span>Slideshow</span>
            </Button>
          )}

          {images.length > 0 && (
            <Button
              id={`${props.idPrefix}-tab-gallery`}
              $active={subPage === 'gallery'}
              href={props.getReplaceUrl('/gallery')}
              shallow
              replace={props.replace}
              data-testid="slideshow-gallery-button"
              onClick={trackLink('Gallery')}
            >
              <FontAwesomeIcon icon={faImages} size={18} />
              <span>Gallery</span>
            </Button>
          )}

          <Button
            id={`${props.idPrefix}-tab-map`}
            $active={urlParts.includes('map')}
            href={props.getReplaceUrl('/map')}
            shallow
            replace={props.replace}
            onClick={trackLink('Map')}
          >
            <FontAwesomeIcon icon={faMapMarkerAlt} size={18} />
            <span>Map</span>
          </Button>

          {propertyTour && tourPath && (
            <Button
              as="button"
              href="/"
              id={`${props.idPrefix}-tab-${tourPath}`}
              $active={subPage === tourPath}
              onClick={handleTourClick}
              type="button"
              data-testid="slideshow-tour-button"
            >
              <FontAwesomeIcon icon={faVideoPlus} size={18} />
              <span>Tour</span>
            </Button>
          )}

          {propertyVideo && (
            <Button
              id={`${props.idPrefix}-tab-${videoPath}`}
              $active={subPage === videoPath}
              href={props.getReplaceUrl(`/${videoPath}`)}
              shallow
              replace={props.replace}
              onClick={trackLink('Video')}
            >
              <FontAwesomeIcon icon={faVideo} size={18} />
              <span>Video</span>
            </Button>
          )}

          {!!propertyFloorPlans?.length && (
            <Button
              id={`${props.idPrefix}-tab-floor-plans`}
              $active={tabId === 'tab-floor-plans'}
              href={props.getReplaceUrl('/floor-plans')}
              shallow
              replace={props.replace}
              onClick={trackLink('Floor Plans')}
            >
              <FontAwesomeIcon icon={faPictureInPicture} size={18} />
              <span>Floor Plans</span>
            </Button>
          )}

          {props.hasSitemap && (
            <Button
              id={`${props.idPrefix}-tab-sitemap`}
              $active={tabId === 'tab-sitemap'}
              href={props.getReplaceUrl('/sitemap')}
              shallow
              replace={props.replace}
              data-testid="slideshow-site-map-button"
              onClick={trackLink('Site Map')}
            >
              <SitemapIcon />
              <span>Site Map</span>
            </Button>
          )}

          {props.hasFeaturedBrochure && (
            <Button
              id={`${props.idPrefix}-tab-brochure`}
              $active={urlParts.includes('brochure')}
              href={props.getReplaceUrl('/brochure/1')}
              shallow
              replace={props.replace}
              data-testid="slideshow-brochure-button"
              onClick={trackLink('Brochure')}
            >
              <FontAwesomeIcon icon={faFilePdf} size={18} />
              Brochure
            </Button>
          )}

          {images.length > 0 &&
            props.property.type === 'property' &&
            props.property.propertyType?.key === 'residential' && (
              <Button
                id={`${props.idPrefix}-tab-shop-the-look`}
                $active={subPage === 'shop-the-look'}
                href={props.getReplaceUrl('/shop-the-look')}
                shallow
                replace={props.replace}
                onClick={trackLink('Shop the Look')}
              >
                <ShopTheLook />
                <span>Shop the Look</span>
                <Pill>NEW</Pill>
              </Button>
            )}
        </Content>
      </Rail>
    </Container>
  );
};

export default SlideshowBar;
